/**
 * Modernize Theme for Recruiter & Candidate Apps
 *
 * This theme is based on the Modernize admin template and provides
 * consistent styling across the Recruiter and Candidate applications.
 * All colors meet WCAG 2.1 AA contrast requirements (4.5:1 for text, 3:1 for UI).
 *
 * Color Palette:
 * - Primary: #4A70DE (blue)
 * - Secondary: #49BEFF (cyan) — dark text contrastText
 * - Success: #13DEB9 (green) — dark text contrastText
 * - Error: #FA896B (coral) — dark text contrastText
 * - Warning: #FFAE1F (orange) — dark text contrastText
 * - Info: #3B76CA (blue)
 */
import { createTheme } from '@mui/material/styles';
// ============================================================================
// Color Palette
// ============================================================================
export const modernizeColors = {
    primary: {
        main: '#4A70DE',
        light: '#ECF2FF',
        dark: '#4568CF',
        contrastText: '#ffffff',
        border: '#c6d6ff',
        hover: '#3D62CB',
    },
    secondary: {
        main: '#49BEFF',
        light: '#E8F7FF',
        dark: '#2877A3',
        contrastText: '#2A3547',
    },
    success: {
        main: '#13DEB9',
        light: '#E6FFFA',
        dark: '#05826C',
        contrastText: '#2A3547',
        border: '#80f0d7',
        darker: '#006D59',
    },
    info: {
        main: '#3B76CA',
        light: '#EBF3FE',
        dark: '#376FBD',
        contrastText: '#ffffff',
    },
    error: {
        main: '#FA896B',
        light: '#FDEDE8',
        dark: '#A65742',
        contrastText: '#2A3547',
    },
    warning: {
        main: '#FFAE1F',
        light: '#FEF5E5',
        dark: '#99660B',
        contrastText: '#2A3547',
        border: '#ffe0a0',
        badgeText: '#7C4A00',
    },
    accent: {
        main: '#7C4DFF',
        light: '#EDE7F6',
        dark: '#6a3de8',
        border: '#d1c4e9',
        contrastText: '#ffffff',
    },
    grey: {
        50: '#FAFBFC',
        100: '#F2F6FA',
        200: '#EAEFF4',
        300: '#8696AB',
        400: '#657793',
        500: '#5A6A85',
        600: '#2A3547',
    },
    text: {
        primary: '#2A3547',
        secondary: '#5A6A85',
        muted: '#7A8699',
        disabled: '#6E7B8F',
        dark: '#1f2937',
        body: '#374151',
        light: '#4b5563',
    },
    background: {
        default: '#ffffff',
        paper: '#ffffff',
        page: '#F4F5F6',
    },
    divider: '#C7CFDB',
    borderColor: '#C7CFDB',
    shadowColor: '#DEE3E9',
    action: {
        hover: '#E7ECF4',
        hoverOpacity: 0.06,
        disabledBackground: 'rgba(73,82,88,0.12)',
        disabled: '#AEB6C2',
    },
    hero: {
        gradientStart: '#098570',
        gradientEnd: '#1F7AAD',
    },
};
// ============================================================================
// Typography
// ============================================================================
const typography = {
    fontFamily: '\'Rubik\', \'Roboto\', \'Helvetica\', \'Arial\', sans-serif',
    h1: {
        fontWeight: 600,
        fontSize: '2.25rem',
        lineHeight: '2.75rem',
    },
    h2: {
        fontWeight: 600,
        fontSize: '1.875rem',
        lineHeight: '2.25rem',
    },
    h3: {
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: '1.75rem',
    },
    h4: {
        fontWeight: 600,
        fontSize: '1.3125rem',
        lineHeight: '1.6rem',
    },
    h5: {
        fontWeight: 600,
        fontSize: '1.125rem',
        lineHeight: '1.6rem',
    },
    h6: {
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: '1.2rem',
    },
    button: {
        textTransform: 'capitalize',
        fontWeight: 400,
    },
    body1: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: '1.334rem',
    },
    body2: {
        fontSize: '0.8125rem',
        letterSpacing: '0rem',
        fontWeight: 400,
        lineHeight: '1.125rem',
    },
    subtitle1: {
        fontSize: '0.875rem',
        fontWeight: 400,
    },
    subtitle2: {
        fontSize: '0.875rem',
        fontWeight: 400,
    },
    caption: {
        fontSize: '0.8125rem',
        fontWeight: 400,
    },
    overline: {
        fontSize: '0.8125rem',
        fontWeight: 600,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
    },
};
// ============================================================================
// Component Overrides
// ============================================================================
const components = {
    MuiCssBaseline: {
        styleOverrides: {
            '*': {
                boxSizing: 'border-box',
            },
            html: {
                MozOsxFontSmoothing: 'grayscale',
                WebkitFontSmoothing: 'antialiased',
                height: '100%',
                width: '100%',
            },
            body: {
                height: '100%',
                margin: 0,
                padding: 0,
            },
            '#root': {
                height: '100%',
            },
            '.MuiPaper-elevation9, .MuiPopover-root .MuiPaper-elevation': {
                boxShadow: 'rgb(222 227 233 / 60%) 0px 0px 2px 0px, rgb(222 227 233 / 50%) 0px 12px 24px -4px !important',
            },
        },
    },
    MuiButton: {
        styleOverrides: {
            root: {
                borderRadius: '7px',
                textTransform: 'capitalize',
                fontWeight: 500,
            },
            containedPrimary: {
                '&:hover': {
                    backgroundColor: modernizeColors.primary.dark,
                },
            },
            outlinedPrimary: {
                borderColor: modernizeColors.primary.main,
                '&:hover': {
                    backgroundColor: modernizeColors.primary.light,
                    borderColor: modernizeColors.primary.main,
                },
            },
        },
        defaultProps: {
            disableElevation: true,
        },
    },
    MuiCard: {
        styleOverrides: {
            root: {
                borderRadius: '7px',
                boxShadow: 'rgb(222 227 233 / 60%) 0px 0px 2px 0px, rgb(222 227 233 / 50%) 0px 8px 20px -4px',
            },
        },
        defaultProps: {
            elevation: 9,
        },
    },
    MuiCardContent: {
        styleOverrides: {
            root: {
                padding: '30px',
                '&:last-child': {
                    paddingBottom: '30px',
                },
            },
        },
    },
    MuiPaper: {
        styleOverrides: {
            root: {
                backgroundImage: 'none',
            },
        },
    },
    MuiAppBar: {
        styleOverrides: {
            root: {
                boxShadow: 'none',
                backdropFilter: 'blur(4px)',
            },
        },
        defaultProps: {
            color: 'inherit',
        },
    },
    MuiDrawer: {
        styleOverrides: {
            paper: {
                boxSizing: 'content-box',
                borderLeft: 'none',
                borderTop: 'none',
                borderBottom: 'none',
                borderRight: `1px solid ${modernizeColors.divider}`,
            },
        },
    },
    MuiListItemButton: {
        styleOverrides: {
            root: {
                borderRadius: '8px',
                marginBottom: '2px',
                padding: '10px 12px',
                '&.Mui-selected': {
                    backgroundColor: modernizeColors.primary.main,
                    color: '#ffffff',
                    '&:hover': {
                        backgroundColor: modernizeColors.primary.main,
                    },
                    '& .MuiListItemIcon-root': {
                        color: '#ffffff',
                    },
                },
                '&:hover': {
                    backgroundColor: `${modernizeColors.primary.main}20`,
                },
            },
        },
    },
    MuiListItemIcon: {
        styleOverrides: {
            root: {
                minWidth: '36px',
                color: modernizeColors.text.secondary,
            },
        },
    },
    MuiListSubheader: {
        styleOverrides: {
            root: {
                backgroundColor: 'transparent',
                fontWeight: 700,
                fontSize: '0.8125rem',
                letterSpacing: '0.5px',
                lineHeight: '2.5rem',
                color: modernizeColors.text.secondary,
            },
        },
    },
    MuiChip: {
        styleOverrides: {
            root: {
                borderRadius: '6px',
                fontWeight: 500,
            },
            filled: {
                '&.MuiChip-colorPrimary': {
                    backgroundColor: modernizeColors.primary.light,
                    color: modernizeColors.primary.dark,
                },
                '&.MuiChip-colorSecondary': {
                    backgroundColor: modernizeColors.secondary.light,
                    color: modernizeColors.secondary.dark,
                },
                '&.MuiChip-colorSuccess': {
                    backgroundColor: modernizeColors.success.light,
                    color: modernizeColors.success.dark,
                },
                '&.MuiChip-colorError': {
                    backgroundColor: modernizeColors.error.light,
                    color: modernizeColors.error.dark,
                },
                '&.MuiChip-colorWarning': {
                    backgroundColor: modernizeColors.warning.light,
                    color: modernizeColors.warning.dark,
                },
                '&.MuiChip-colorInfo': {
                    backgroundColor: modernizeColors.info.light,
                    color: modernizeColors.info.dark,
                },
            },
        },
    },
    MuiTextField: {
        styleOverrides: {
            root: {
                '& .MuiOutlinedInput-root': {
                    borderRadius: '7px',
                    '& fieldset': {
                        borderColor: modernizeColors.grey[300],
                    },
                    '&:hover fieldset': {
                        borderColor: modernizeColors.primary.main,
                    },
                    '&.Mui-focused fieldset': {
                        borderColor: modernizeColors.primary.main,
                    },
                },
            },
        },
    },
    MuiOutlinedInput: {
        styleOverrides: {
            root: {
                borderRadius: '7px',
                '& fieldset': {
                    borderColor: modernizeColors.grey[300],
                },
                '&:hover fieldset': {
                    borderColor: modernizeColors.primary.main,
                },
                '&.Mui-focused fieldset': {
                    borderColor: modernizeColors.primary.main,
                },
            },
        },
    },
    MuiSelect: {
        styleOverrides: {
            root: {
                borderRadius: '7px',
            },
        },
    },
    MuiAvatar: {
        styleOverrides: {
            root: {
                fontSize: '1rem',
                fontWeight: 600,
            },
        },
    },
    MuiTooltip: {
        styleOverrides: {
            tooltip: {
                backgroundColor: modernizeColors.grey[600],
                fontSize: '0.8125rem',
                borderRadius: '4px',
            },
        },
    },
    MuiDialog: {
        styleOverrides: {
            paper: ({ theme }) => ({
                borderRadius: '12px',
                // On phones: take the full viewport so dialogs don't waste space inside a tiny card.
                [theme.breakpoints.down('sm')]: {
                    margin: 0,
                    width: '100%',
                    maxWidth: '100%',
                    height: '100%',
                    maxHeight: '100%',
                    borderRadius: 0,
                },
            }),
            paperFullScreen: {
                borderRadius: 0,
            },
        },
    },
    MuiDialogTitle: {
        styleOverrides: {
            root: ({ theme }) => ({
                fontSize: '1.125rem',
                fontWeight: 600,
                [theme.breakpoints.down('sm')]: {
                    padding: '12px 16px',
                },
            }),
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: ({ theme }) => ({
                // MUI zeroes DialogContent's top padding when it sits directly
                // after a DialogTitle. Re-declare with matching specificity so
                // our rule wins by source order — keeps consistent breathing
                // room across every dialog in the app.
                '.MuiDialogTitle-root + &': { paddingTop: '20px' },
                [theme.breakpoints.down('sm')]: {
                    padding: '12px 16px',
                    '.MuiDialogTitle-root + &': { paddingTop: '12px' },
                },
            }),
        },
    },
    MuiDialogActions: {
        styleOverrides: {
            root: ({ theme }) => ({
                [theme.breakpoints.down('sm')]: {
                    padding: '8px 16px 12px',
                },
            }),
        },
    },
    MuiTab: {
        styleOverrides: {
            root: {
                textTransform: 'capitalize',
                fontWeight: 500,
                minHeight: '48px',
            },
        },
    },
    MuiTabs: {
        styleOverrides: {
            indicator: {
                height: '3px',
                borderRadius: '3px',
            },
        },
    },
    MuiAlert: {
        styleOverrides: {
            root: {
                borderRadius: '7px',
            },
            standardSuccess: {
                backgroundColor: modernizeColors.success.light,
                color: modernizeColors.success.dark,
                '& .MuiAlert-icon': {
                    color: modernizeColors.success.main,
                },
            },
            standardError: {
                backgroundColor: modernizeColors.error.light,
                color: modernizeColors.error.dark,
                '& .MuiAlert-icon': {
                    color: modernizeColors.error.main,
                },
            },
            standardWarning: {
                backgroundColor: modernizeColors.warning.light,
                color: modernizeColors.warning.dark,
                '& .MuiAlert-icon': {
                    color: modernizeColors.warning.main,
                },
            },
            standardInfo: {
                backgroundColor: modernizeColors.info.light,
                color: modernizeColors.info.dark,
                '& .MuiAlert-icon': {
                    color: modernizeColors.info.main,
                },
            },
        },
    },
    MuiLinearProgress: {
        styleOverrides: {
            root: {
                borderRadius: '4px',
                height: '6px',
            },
        },
    },
    MuiDivider: {
        styleOverrides: {
            root: {
                borderColor: modernizeColors.divider,
            },
        },
    },
    MuiMenu: {
        styleOverrides: {
            paper: {
                borderRadius: '8px',
                boxShadow: 'rgb(222 227 233 / 60%) 0px 0px 2px 0px, rgb(222 227 233 / 50%) 0px 8px 20px -4px',
            },
        },
    },
    MuiMenuItem: {
        styleOverrides: {
            root: {
                borderRadius: '6px',
                margin: '2px 8px',
                padding: '8px 12px',
                '&:hover': {
                    backgroundColor: modernizeColors.action.hover,
                },
            },
        },
    },
    MuiBadge: {
        styleOverrides: {
            badge: {
                fontWeight: 600,
                fontSize: '0.8125rem',
            },
        },
    },
    MuiIconButton: {
        styleOverrides: {
            root: {
                '&:hover': {
                    backgroundColor: `${modernizeColors.primary.main}15`,
                },
            },
        },
    },
    MuiTableHead: {
        styleOverrides: {
            root: {
                '& .MuiTableCell-root': {
                    fontWeight: 600,
                    color: modernizeColors.text.primary,
                },
            },
        },
    },
    MuiTableCell: {
        styleOverrides: {
            root: {
                borderColor: modernizeColors.divider,
                padding: '16px',
            },
        },
    },
};
// ============================================================================
// Theme Creation
// ============================================================================
/**
 * Create the Modernize theme with all configurations
 */
export const modernizeTheme = createTheme({
    direction: 'ltr',
    palette: {
        mode: 'light',
        primary: modernizeColors.primary,
        secondary: modernizeColors.secondary,
        success: modernizeColors.success,
        info: modernizeColors.info,
        error: modernizeColors.error,
        warning: modernizeColors.warning,
        grey: modernizeColors.grey,
        text: modernizeColors.text,
        background: modernizeColors.background,
        divider: modernizeColors.divider,
        action: modernizeColors.action,
    },
    typography,
    components,
    shape: {
        borderRadius: 7,
    },
});
// ============================================================================
// Sidebar Constants
// ============================================================================
export const sidebarConfig = {
    width: '270px', // Content width
    drawerWidth: '271px', // Total drawer width (content + 1px border)
    collapseWidth: '80px',
    themeColor: modernizeColors.primary.main,
    themeSecondaryColor: modernizeColors.secondary.main,
    textColor: modernizeColors.text.primary,
    secondaryTextColor: modernizeColors.text.secondary,
    headerHeight: '70px',
    containerMaxWidth: '1400px',
};
// ============================================================================
// Scrollbar Styles (for sidebars and scrollable areas)
// ============================================================================
export const scrollbarStyles = {
    '&::-webkit-scrollbar': {
        width: '7px',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#eff2f7',
        borderRadius: '15px',
    },
};
export default modernizeTheme;
// ============================================================================
// Recruiter design tokens (RT)
// ============================================================================
//
// `RT` is the canonical recruiter-app token object (used directly in non-React
// scopes, static sx props, utilities). The same object is also installed into
// the MUI palette as `palette.recruiter`, so components can read it via
// `useTheme()` / sx callback:
//
//   const t = useTheme();      // → t.palette.recruiter.navy
//   sx={(t) => ({ color: t.palette.recruiter.navy })}
//
// Both forms reference the SAME object. No drift.
export const RT = {
    // ── Primary palette (navy → Modernize primary) ─────────────────────────
    navy: modernizeColors.primary.main, // #4A70DE
    navyHov: modernizeColors.primary.hover, // #3D62CB
    navyDeep: modernizeColors.primary.dark, // #4568CF
    navyMuted: `${modernizeColors.primary.main}14`, // 8% opacity
    // ── Emerald — success / AI positive ────────────────────────────────────
    emerald: modernizeColors.success.main, // #13DEB9
    emeraldHov: modernizeColors.success.dark, // #05826C
    emeraldLt: modernizeColors.success.main,
    emeraldBg: modernizeColors.success.light, // #E6FFFA
    emeraldBdr: modernizeColors.success.border, // #80f0d7
    // ── Success semantic shades (green scale) ──────────────────────────────
    successBg: '#f0fdf4',
    successBgLight: '#ecfdf5',
    successBorder: '#a7f3d0',
    successBdrMid: '#d1fae5',
    successBdrBold: '#6ee7b7',
    successAccent: '#34d399',
    successText: '#065f46',
    successTextDark: '#064e3b',
    // ── Semantic — alert states ────────────────────────────────────────────
    urgent: modernizeColors.error.main, // #FA896B
    urgentBg: modernizeColors.error.light, // #FDEDE8
    urgentBdr: '#fecaca',
    // ── Error semantic shades (red scale) ──────────────────────────────────
    errorBg: '#fef2f2',
    errorBorder: '#fecaca',
    errorMid: '#f87171',
    error: '#ef4444',
    errorDark: '#dc2626',
    errorDarkText: '#b91c1c',
    errorCardBg: '#fff5f5',
    // ── Amber / warning shades ─────────────────────────────────────────────
    amber: modernizeColors.warning.main, // #FFAE1F
    amberBg: modernizeColors.warning.light, // #FEF5E5
    amberBdr: modernizeColors.warning.border, // #ffe0a0
    amberText: '#92400e',
    amberLightBg: '#fffbeb',
    amberLight: '#fef3c7',
    amberBorder: '#fde68a',
    amberMain: '#f59e0b',
    amberDark: '#d97706',
    amberDarker: '#b45309',
    amberAccent: '#fbbf24',
    amberDeepText: '#78350f',
    // ── Blue / info shades ─────────────────────────────────────────────────
    blue: modernizeColors.info.main, // #3B76CA
    blueBg: modernizeColors.info.light, // #EBF3FE
    blueBdr: '#dbeafe',
    blueText: '#1e40af',
    blueLightBg: '#eff6ff',
    blueLightBdr: '#bfdbfe',
    blueMid: '#93c5fd',
    blueMain: '#3b82f6',
    blueDark: '#1d4ed8',
    blueAccent: '#2563eb',
    // ── Violet / accent shades ─────────────────────────────────────────────
    violet: modernizeColors.accent.main, // #7C4DFF
    violetBg: modernizeColors.accent.light, // #EDE7F6
    violetBdr: modernizeColors.accent.border, // #d1c4e9
    violetMain: '#7c3aed',
    violetDark: '#5b21b6',
    violetDeep: '#4c1d95',
    violetIndigo: '#6366f1',
    // ── Neutral scale ─────────────────────────────────────────────────────
    text: modernizeColors.text.primary, // #2A3547
    textSub: modernizeColors.text.secondary, // #5A6A85
    textMuted: modernizeColors.text.muted, // #8696AB
    textDisabl: modernizeColors.text.disabled, // #d1d5db
    textDark: modernizeColors.text.dark, // #1f2937
    textBody: modernizeColors.text.body, // #374151
    textLight: modernizeColors.text.light, // #4b5563
    border: modernizeColors.divider, // #D4DAE2
    borderHov: '#bcc3cc',
    bg: modernizeColors.grey[50], // #FAFBFC
    bgHov: modernizeColors.grey[100], // #F2F6FA
    bgSubtle: '#f3f4f6',
    bgSlate: '#f1f5f9',
    bgTodayCol: '#f9fffe',
    surface: '#ffffff',
    // ── Tag chip color scales ──────────────────────────────────────────────
    tagVioletBg: '#f5f3ff', tagVioletText: '#7c3aed', tagVioletBdr: '#ddd6fe',
    tagBlueBg: '#eff6ff', tagBlueText: '#2563eb', tagBlueBdr: '#bfdbfe',
    tagTealBg: '#f0fdfa', tagTealText: '#0d9488', tagTealBdr: '#99f6e4',
    tagOrangeBg: '#fff7ed', tagOrangeText: '#ea580c', tagOrangeBdr: '#fed7aa',
    tagIndigoBg: '#eef2ff', tagIndigoText: '#4f46e5', tagIndigoBdr: '#c7d2fe',
    tagPinkBg: '#fdf2f8', tagPinkText: '#db2777', tagPinkBdr: '#fbcfe8',
    tagAmberBg: '#fefce8', tagAmberText: '#d97706', tagAmberBdr: '#fde68a',
    tagRoseBg: '#fff1f2', tagRoseText: '#e11d48', tagRoseBdr: '#fecdd3',
    tagEmeraldBg: '#ecfdf5', tagEmeraldText: '#059669', tagEmeraldBdr: '#a7f3d0',
    tagCyanBg: '#ecfeff', tagCyanText: '#0891b2', tagCyanBdr: '#a5f3fc',
    // ── Motion ────────────────────────────────────────────────────────────
    easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    dur: '150ms',
};
/** Shared sx style for outlined header action buttons (Post New Job, Add Candidates, etc.) */
export const headerButtonSx = {
    textTransform: 'none',
    fontSize: '0.8125rem',
    fontWeight: 700,
    height: '34px',
    px: '12px',
    borderRadius: '8px',
    bgcolor: 'transparent',
    color: RT.navy,
    border: `1px solid ${RT.navy}`,
    '&:hover': { bgcolor: RT.navyMuted, borderColor: RT.navyHov },
};
// ─── Recruiter MUI theme ─────────────────────────────────────────────────────
// Extends the shared Modernize theme with the recruiter palette node.
export const recruiterTheme = createTheme(modernizeTheme, {
    palette: {
        recruiter: RT,
    },
});
